import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import classNames from "classnames";

import {
  resendVerification,
  updateAndverifyPhone,
  updatePhone,
} from "features/Account/service";
import {
  codeInitialValues,
  codeValidationSchema,
  phoneInitialValues,
  phoneValidationSchema,
} from "./validation";
import { parseErrorMessage } from "utils";
import { Loader } from "components/Loader";
import { setUserDetails, toast } from "Layout/slice";
import { userDetailSelect } from "Layout/slice/selector";
import { ChangePhoneContainer } from "./ChangePhone.styles";
import { Form, Input } from "components/Form";
import { Button } from "components/Button";

export const ChangePhone = ({ onSuccess, handleEditPhoneVisible = false }) => {
  const dispatch = useDispatch();
  const profileDetails = useSelector(userDetailSelect);

  const [showCode, setShowCode] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [seconds, setSeconds] = useState(0);
  const [isPhoneInputTouched, setIsPhoneInputTouched] = useState(false); // New state for tracking input focus

  const screenWidth = window.innerWidth;

  useEffect(() => {
    let intervalId;

    if (seconds > 0) {
      setResendDisabled(true);
      intervalId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else {
      clearInterval(intervalId);
      setResendDisabled(false);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [seconds]);

  const handleResendClick = async () => {
    try {
      await resendVerification({
        type: "sms",
        email: profileDetails.email,
      });
      setSeconds(20);
      dispatch(
        toast({
          type: "info",
          body: "The code has been sent!",
        })
      );
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const handleSubmitPhone = async (values) => {
    try {
      await updatePhone({
        phone: `+${values.phone}`,
      });
      setSeconds(20);
      setShowCode(true);
      setIsPhoneInputTouched(false);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const handleCodeSubmit = async (values) => {
    try {
      const unmaskedPhone = phoneFormik.values.phone.replace(/\D/g, "");

      const response = await updateAndverifyPhone({
        mfa_code: values.code,
        phone: `+${unmaskedPhone}`,
      });
      setShowCode(false);
      dispatch(setUserDetails({ ...profileDetails, ...response }));
      onSuccess();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const phoneFormik = useFormik({
    validationSchema: phoneValidationSchema,
    initialValues: phoneInitialValues(profileDetails),
    onSubmit: handleSubmitPhone,
  });

  const codeFormik = useFormik({
    onSubmit: handleCodeSubmit,
    initialValues: codeInitialValues,
    validationSchema: codeValidationSchema,
  });

  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

  return (
    <ChangePhoneContainer className="change-phone-container">
      <Row className="w-100 m-0 change-phone-row">
        <Col
          md={showCode ? 10 : 12}
          className={showCode ? " phone-col" : "p-0"}
        >
          <Form
            className="phone-form w-100 d-flex"
            onSubmit={phoneFormik.handleSubmit}
          >
            <span className="control">
              <PhoneInput
                placeholder="Phone"
                country="us"
                inputProps={{
                  name: "phone",
                }}
                onFocus={() => {
                  setIsPhoneInputTouched(true);
                  handleEditPhoneVisible();
                }}
                enableSearch
                {...phoneFormik.getFieldProps("phone")}
                onChange={(phone) => {
                  phoneFormik.setFieldValue("phone", phone);
                }}
                inputClass={classNames("phone-form-field", {
                  "is-invalid":
                    phoneFormik.touched.phone && phoneFormik.errors.phone,
                })}
                buttonClass={classNames({
                  "is-invalid":
                    phoneFormik.touched.phone && phoneFormik.errors.phone,
                })}
              />
              {!showCode && isPhoneInputTouched && (
                <Button
                  type="submit"
                  size={"small"}
                  disabled={!phoneFormik.isValid || !phoneFormik.values.phone}
                >
                  {phoneFormik.isSubmitting ? (
                    <Loader className="on-btn" />
                  ) : (
                    "Save"
                  )}
                </Button>
              )}
            </span>
          </Form>
        </Col>
        <Col md={2} className="code-col p-0 ">
          {showCode && (
            <Form
              className="code-form w-100 d-flex"
              onSubmit={codeFormik.handleSubmit}
            >
              <div className="code-input ">
                <span>
                  <Input
                    label="Otp code"
                    type="text"
                    placeholder="Code"
                    {...codeFormik.getFieldProps("code")}
                  />
                  {codeFormik.touched.code && codeFormik.errors.code ? (
                    <div className="form-error">{codeFormik.errors.code}</div>
                  ) : null}
                </span>
              </div>
              <div>
                <div className="verify-button-wrapper">
                  <Button
                    type="submit"
                    disabled={!codeFormik.isValid}
                    className={screenWidth < 450 ? "transparent-btn" : ""}
                  >
                    {codeFormik.isSubmitting ? (
                      <Loader className="on-btn" />
                    ) : screenWidth > 450 ? (
                      "Verify"
                    ) : (
                      <div className="check" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    className={classNames("button-outlined resend-btn", {
                      "resend-seconds": seconds > 0,
                    })}
                    disabled={resendDisabled}
                    onClick={handleResendClick}
                  >
                    {seconds > 0 ? `Resend in ${formattedSeconds}` : "Resend"}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Col>
      </Row>
    </ChangePhoneContainer>
  );
};

ChangePhone.propTypes = {
  onSuccess: PropTypes.func,
  handleEditPhoneVisible: PropTypes.any,
};
